import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useId, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { authClient } from "@/lib/auth-client";
import { getSoundEffects } from "@/services/SoundEffects";

export const Route = createFileRoute("/_layout/signin")({
  component: SignIn,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      redirect: (search.redirect as string) || undefined,
    };
  },
});

function SignIn() {
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();
  const titleId = useId();
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { signInWithApple } = useAuth();
  const isLocalhost =
    typeof window !== "undefined" && window.location.hostname === "localhost";

  const handleAppleSignIn = async () => {
    setError("");
    setIsLoading(true);

    try {
      const result = await signInWithApple(redirect);

      if (!result.success) {
        setError(result.error || "Failed to sign in with Apple");
      }
      // If successful, the OAuth flow will redirect automatically
    } catch (_err) {
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickLogin = async () => {
    setError("");
    setIsLoading(true);

    try {
      getSoundEffects().play("buttonDown");

      // Try to sign in first
      const signInResult = await authClient.signIn.email({
        email: "test@example.com",
        password: "testpassword123",
      });

      if (signInResult.error) {
        // If sign in fails, try to create the account
        // Better Auth returns INVALID_EMAIL_OR_PASSWORD for non-existent users
        if (signInResult.error.code === "INVALID_EMAIL_OR_PASSWORD" ||
            signInResult.error.message?.includes("User not found")) {
          console.log("User not found, attempting to create account...");

          const signUpResult = await authClient.signUp.email({
            email: "test@example.com",
            password: "testpassword123",
            name: "Test User",
          });

          if (signUpResult.error) {
            console.error("Signup error:", signUpResult.error);
            setError(signUpResult.error.message || "Failed to create test account");
          } else {
            console.log("Account created successfully, attempting to sign in...");
            // Now try to sign in again
            const retrySignIn = await authClient.signIn.email({
              email: "test@example.com",
              password: "testpassword123",
            });

            if (retrySignIn.error) {
              console.error("Sign in after signup error:", retrySignIn.error);
              setError(retrySignIn.error.message || "Failed to sign in after creating account");
            } else {
              console.log("Successfully signed in!");
              // Redirect to the specified path or home
              if (redirect) {
                window.location.href = redirect;
              } else {
                navigate({ to: "/" });
              }
            }
          }
        } else {
          console.error("Sign in error:", signInResult.error);
          setError(signInResult.error.message || "Failed to sign in");
        }
      } else {
        console.log("Successfully signed in!");
        // Redirect to the specified path or home
        if (redirect) {
          window.location.href = redirect;
        } else {
          navigate({ to: "/" });
        }
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-200px)] flex-col items-center justify-center py-12">
      <div className="w-full max-w-md space-y-8">
        {/* Back Button */}
        <button
          className="flex items-center gap-2 font-serif text-gray-600 hover:text-black transition-colors duration-200 cursor-pointer"
          onClick={() => {
            getSoundEffects().play("buttonDown");
            navigate({ to: "/" });
          }}
        >
          <ArrowLeft className="h-5 w-5" />
          Back
        </button>

        {/* Page Title */}
        <div className="text-center">
          <h1 className="font-serif-600 text-4xl text-black mb-4">
            Sign In
          </h1>
        </div>

        {/* Sign In Form */}
        <div className="space-y-6">

          {/* Apple Sign In Button */}
          <button
            className="w-full bg-black text-white py-4 px-6 rounded-lg font-serif hover:bg-gray-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            disabled={isLoading}
            onClick={() => {
              getSoundEffects().play("buttonDown");
              handleAppleSignIn();
            }}
            type="button"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <svg
                  aria-labelledby={titleId}
                  className="h-6 w-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <title id={titleId}>Apple logo</title>
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
                <span>Sign in with Apple</span>
              </>
            )}
          </button>

          {/* Quick Login for Development */}
          {isLocalhost && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-3 text-gray-500 font-serif">
                    Development Only
                  </span>
                </div>
              </div>

              <button
                className="w-full bg-orange-500 text-white py-4 px-6 rounded-lg font-serif hover:bg-orange-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                disabled={isLoading}
                onClick={() => {
                  getSoundEffects().play("buttonDown");
                  handleQuickLogin();
                }}
                type="button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <span>Quick Login (Dev Only)</span>
                )}
              </button>
            </>
          )}

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4">
              <p className="text-center text-red-600 text-sm font-serif">{error}</p>
            </div>
          )}
        </div>

        {/* Privacy Note */}
        <div className="text-center">
          <p className="text-gray-600 text-sm font-serif">
            Your Apple ID is used only for authentication.
            <br />
            We never store your Apple credentials.
          </p>
        </div>

        {/* Info Note */}
        <div className="text-center">
          <p className="text-gray-500 text-sm font-serif">
            Sign in to access your personal stations, save favorites, and
            broadcast your own content.
          </p>
        </div>
      </div>
    </div>
  );
}
